import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Award, Briefcase, Clock, Send, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Page, Loading, ErrorBox, Empty } from "@/components/site/Common";
import { TradeBadge, Stars } from "@/components/site/Badges";
import { eur, fdate, initials, tradeName } from "@/lib/hz";
import { get } from "@/lib/api";
import { useMe } from "@/hooks/useMe";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4"><Icon className="size-5 text-orange-600" /><div className="mt-2 font-heading text-2xl font-extrabold">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
);

export default function ProDetail() {
  const { id } = useParams();
  const { data: me } = useMe();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["pros", id], queryFn: () => get(`/pros/${id}`) });
  if (isLoading) return <Page><Loading /></Page>;
  if (isError || !p) return <Page><ErrorBox text="No encontramos este profesional." /></Page>;
  const canRequest = !me || me.role === "cliente";
  return (
    <Page>
      <Link to="/profesionales" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft className="size-4" />Directorio</Link>
      <div className="mt-6 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
            <div className="flex flex-wrap items-start gap-4">
              <span className="grid size-16 place-items-center rounded-full bg-slate-900 font-heading text-xl font-bold text-white">{initials(p.name)}</span>
              <div className="flex-1">
                <h1 data-testid="pro-name" className="font-heading text-3xl font-extrabold text-slate-900">{p.name}</h1>
                <div className="mt-1 flex items-center gap-1 text-slate-500"><MapPin className="size-4" />{p.city}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">{p.trades.map((t) => <TradeBadge key={t} trade={t} />)}</div>
              </div>
              <div className="text-right"><div className="font-heading text-2xl font-extrabold">{eur(p.hourly_rate)}</div><div className="text-xs text-slate-500">por hora</div></div>
            </div>
            <p className="mt-6 text-slate-700">{p.bio}</p>
            <div className="mt-4"><Stars value={p.rating} count={p.reviews_count} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat icon={Briefcase} label="Trabajos realizados" value={p.jobs_done} />
            <Stat icon={Clock} label="Puntualidad" value={p.punctuality != null ? `${p.punctuality}%` : "—"} />
            <Stat icon={Award} label="Insignias" value={p.badges.length} />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Valoraciones</h2>
            <div className="mt-4 space-y-3" data-testid="pro-reviews">
              {!p.reviews?.length ? <Empty>Aún no tiene valoraciones.</Empty> : p.reviews.map((r, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2"><Stars value={r.rating} size="size-3.5" /><span className="text-xs text-slate-500">{fdate(r.created_at)}</span></div>
                  <p className="mt-2 text-slate-700">“{r.comment}”</p>
                  <div className="mt-2 text-xs text-slate-500">{r.client_name} · {r.job_title}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <aside className="space-y-4">
          {canRequest && (
            <div className="rounded-3xl bg-slate-950 p-6 text-white">
              <div className="font-heading text-xl font-bold">¿Trabajamos juntos?</div>
              <p className="mt-2 text-sm text-slate-300">Envía tu solicitud directa a {p.name.split(" ")[0]}. Te responderá con precio para aceptar o rechazar.</p>
              <Button asChild size="lg" className="mt-5 w-full rounded-xl"><Link to={`/crear-solicitud?pro=${p.id}&oficio=${p.trades[0]}`} data-testid="request-direct-btn"><Send />Solicitar a {p.name.split(" ")[0]}</Link></Button>
            </div>
          )}
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="font-heading text-lg font-bold">Insignias de formación</div>
            <div className="mt-3 space-y-2">
              {!p.badges.length ? <p className="text-sm text-slate-500">Todavía sin insignias.</p> : p.badges.map((b) => (
                <div key={b.course_id} className="flex items-center gap-3 rounded-xl bg-violet-50 p-3"><Award className="size-5 text-violet-600" /><div><div className="text-sm font-semibold text-slate-900">{b.name}</div><div className="text-xs text-slate-500">{tradeName(b.trade)} · {fdate(b.earned_at)}</div></div></div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </Page>
  );
}
